import Vue from "vue";
import {
  canonicalizeKonekoUsername,
  formatKonekoAPlanningMemberSelectLabel,
  isKonekoMemberExcludedFromPersonalBudget,
  konekoAAdminUsernames,
  listKonekoAPlanningMemberKeys,
  resolveKonekoAPlanningUsernameKey,
} from "@/config/konekoAConfig";
import {STORAGE_VIEW_AS_USERNAME} from "@/constants/dashboardConstants";

/**
 * Tracks admin "View as" preview across routes (JWT user unchanged).
 */
export const dashboardViewAsState = Vue.observable({
  /** Canonical planning roster key when previewing someone other than signed-in user. */
  viewingAsPlanningKeyNonSelf: "",
  /** Bumped when local visibility overrides change (reactivity for getKonekoCapabilities). */
  visibilityOverridesRev: 0,
});

export function bumpKonekoVisibilityOverrides() {
  dashboardViewAsState.visibilityOverridesRev += 1;
}

export function isKonekoViewAsAdmin(profile) {
  const key = canonicalizeKonekoUsername(profile && profile.username);
  if (!key) return false;
  return konekoAAdminUsernames.some((a) => String(a).toLowerCase() === key);
}

export function readStoredViewAsUsername() {
  try {
    const raw = sessionStorage.getItem(STORAGE_VIEW_AS_USERNAME);
    return raw ? String(raw).trim() : "";
  } catch {
    return "";
  }
}

export function writeStoredViewAsUsername(username) {
  try {
    if (username == null || String(username).trim() === "") {
      sessionStorage.removeItem(STORAGE_VIEW_AS_USERNAME);
    } else {
      sessionStorage.setItem(STORAGE_VIEW_AS_USERNAME, String(username).trim());
    }
  } catch {
    /* ignore */
  }
}

export function clearStoredViewAsUsername() {
  writeStoredViewAsUsername("");
  dashboardViewAsState.viewingAsPlanningKeyNonSelf = "";
}

export function buildViewAsSelectOptions(monthKey) {
  const mk =
    monthKey ||
    (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    })();
  const opts = [{value: "", text: "Yourself"}];
  for (const key of listKonekoAPlanningMemberKeys()) {
    if (isKonekoMemberExcludedFromPersonalBudget(mk, key)) continue;
    opts.push({
      value: key,
      text: formatKonekoAPlanningMemberSelectLabel(key),
    });
  }
  return opts;
}

export function viewAsPreviewLabel(planningKey) {
  const key = String(planningKey || "").trim();
  if (!key) return "";
  return formatKonekoAPlanningMemberSelectLabel(key) || key;
}

export function syncDashboardViewAsFromStoreGetter(
  userProfileData,
  isAuthenticated
) {
  if (!isAuthenticated) {
    dashboardViewAsState.viewingAsPlanningKeyNonSelf = "";
    return;
  }
  if (!userProfileData || !String(userProfileData.username || "").trim()) {
    return;
  }
  try {
    const raw = readStoredViewAsUsername();
    const resolved = raw && resolveKonekoAPlanningUsernameKey(raw);
    if (!resolved) {
      dashboardViewAsState.viewingAsPlanningKeyNonSelf = "";
      return;
    }
    const self = canonicalizeKonekoUsername(userProfileData.username);
    const other = canonicalizeKonekoUsername(resolved);
    if (!self || !other || self === other) {
      dashboardViewAsState.viewingAsPlanningKeyNonSelf = "";
      if (raw) writeStoredViewAsUsername("");
      return;
    }
    dashboardViewAsState.viewingAsPlanningKeyNonSelf = resolved;
  } catch {
    dashboardViewAsState.viewingAsPlanningKeyNonSelf = "";
  }
}
